import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { assetUrl } from "../../utils/api";
import { secondhandGet, secondhandPost, secondhandUploadImage } from "../../api/secondhandApi";
import chinaRegions from "../chinaRegions.json";
import GoodsChannelTabs from "../shop/GoodsChannelTabs";

const empty = {
  title: "",
  description: "",
  category: "books",
  condition: "good",
  intent: "negotiate",
  price_note: "",
  province: "",
  city: "",
  district: "",
};

export default function SecondhandNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = Number(localStorage.getItem("user_id") || 0);
  const fileRef = useRef(null);

  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: "发布闲置 - 生活好物",
    metaDescription: "发布旧书或闲置物品，同城会员可私信联系你。",
    path: "/secondhand/new",
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [userId, navigate, location.pathname]);

  useEffect(() => {
    if (!userId) return;
    secondhandGet("/meta").then(setMeta).catch(() => {});
    fetch(`/api/profile/${userId}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data || data.detail) return;
        setForm((prev) => ({
          ...prev,
          province: data.province || prev.province,
          city: data.city || prev.city,
          district: data.district || prev.district,
        }));
      })
      .catch(() => {});
  }, [userId]);

  const cities = useMemo(() => {
    const p = chinaRegions.find((item) => item.name === form.province);
    return p?.children || [];
  }, [form.province]);

  const districts = useMemo(() => {
    const c = cities.find((item) => item.name === form.city);
    return c?.children || [];
  }, [cities, form.city]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 6) {
      alert("最多 6 张图");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const data = await secondhandUploadImage(userId, file);
        if (data.url) setImages((prev) => [...prev, data.url]);
      }
    } catch (err) {
      alert(err.message || "上传失败");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await secondhandPost("/listings", {
        ...form,
        user_id: userId,
        images,
      });
      navigate("/secondhand/mine", {
        state: { flash: "已提交审核，通过后才会公开展示" },
      });
    } catch (err) {
      setError(err.message || "发布失败");
    } finally {
      setSubmitting(false);
    }
  };

  if (!userId) {
    return (
      <div className="min-h-screen flex flex-col bg-rose-50">
        <Header />
        <div className="flex-1 flex items-center justify-center text-gray-400">跳转登录中...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <header className="text-center space-y-3">
            <h1 className="text-2xl font-bold text-gray-800">发布闲置</h1>
            <GoodsChannelTabs />
          </header>

          <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-pink-100 p-6 space-y-5 shadow-sm">
            {error && <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl">{error}</div>}

            <label className="block text-sm">
              <span className="text-gray-600">标题 *</span>
              <input
                required
                value={form.title}
                onChange={(e) => update({ title: e.target.value })}
                className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                placeholder="例如：《沟通的艺术》纸质书"
                maxLength={120}
              />
            </label>

            <label className="block text-sm">
              <span className="text-gray-600">说明</span>
              <textarea
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                rows={4}
                className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                placeholder="成色、取货方式、可交换什么…"
              />
            </label>

            <div className="grid sm:grid-cols-3 gap-3">
              <Select
                label="分类"
                value={form.category}
                onChange={(v) => update({ category: v })}
                options={meta?.categories}
              />
              <Select
                label="成色"
                value={form.condition}
                onChange={(v) => update({ condition: v })}
                options={meta?.conditions}
              />
              <Select
                label="意向"
                value={form.intent}
                onChange={(v) => update({ intent: v })}
                options={meta?.intents}
              />
            </div>

            <label className="block text-sm">
              <span className="text-gray-600">价格 / 交换说明</span>
              <input
                value={form.price_note}
                onChange={(e) => update({ price_note: e.target.value })}
                className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                placeholder="如：免费送 / 面议 20 元 / 换同类书"
              />
            </label>

            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block text-sm">
                <span className="text-gray-600">省</span>
                <select
                  value={form.province}
                  onChange={(e) => update({ province: e.target.value, city: "", district: "" })}
                  className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                >
                  <option value="">请选择</option>
                  {chinaRegions.map((p) => (
                    <option key={p.code} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">市</span>
                <select
                  value={form.city}
                  onChange={(e) => update({ city: e.target.value, district: "" })}
                  className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                  disabled={!form.province}
                >
                  <option value="">请选择</option>
                  {cities.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">区</span>
                <select
                  value={form.district}
                  onChange={(e) => update({ district: e.target.value })}
                  className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
                  disabled={!form.city}
                >
                  <option value="">请选择</option>
                  {districts.map((d) => (
                    <option key={d.code} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">图片（最多 6 张）</span>
                <button
                  type="button"
                  disabled={uploading || images.length >= 6}
                  onClick={() => fileRef.current?.click()}
                  className="text-pink-600"
                >
                  {uploading ? "上传中…" : "添加图片"}
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onUpload} />
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {images.map((url) => (
                    <div key={url} className="relative w-20 h-20">
                      <img src={assetUrl(url)} alt="" className="w-full h-full object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((x) => x !== url))}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-600">
              <input type="checkbox" required className="mt-1" />
              <span>我确认物品合法，愿意通过站内私信沟通，并自行承担线下交接风险。</span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 rounded-full bg-rose-600 text-white font-semibold disabled:opacity-60"
            >
              {submitting ? "提交中…" : "提交审核"}
            </button>

            <p className="text-center text-sm">
              <Link to="/secondhand" className="text-pink-600 hover:underline">
                返回闲置列表
              </Link>
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  const list = options?.length
    ? options
    : [
        { value: "books", label: "旧书" },
        { value: "daily", label: "日用品" },
        { value: "other", label: "其他" },
      ];
  return (
    <label className="block text-sm">
      <span className="text-gray-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-pink-100 rounded-xl px-3 py-2.5"
      >
        {list.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
